import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Spinner } from '../components/common';
import {
  useTranslationProject,
  useUpdateProjectDocs,
  useRegenerateAudio,
} from '../hooks/useTranslationProjects';
import type { ProjectDocs } from '../lib/translation/api';

// Editable project-level guide docs. Keys match both the GraphQL ProjectDocs
// input and the project fields fetched in PROJECT_FIELDS.
const DOCS: { key: keyof ProjectDocs; mono?: boolean; rows: number }[] = [
  { key: 'narrationStyle', rows: 12 },
  { key: 'glossaryMd', mono: true, rows: 8 },
  { key: 'pronunciationMd', mono: true, rows: 6 },
  { key: 'styleMd', rows: 8 },
  { key: 'rulesMd', rows: 8 },
];

// Project-level settings: edit the guide docs that steer translation and
// narration (glossary, voice direction, pronunciation, style, rules), and
// regenerate audio after changing how narration should sound.
export default function TranslationSettingsPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { data: project, isLoading } = useTranslationProject(projectId);
  const save = useUpdateProjectDocs(projectId);
  const regen = useRegenerateAudio(projectId);

  const initial = useMemo<Record<string, string>>(() => {
    const base: Record<string, string> = {};
    if (project) DOCS.forEach(({ key }) => (base[key] = project[key] ?? ''));
    return base;
  }, [project]);

  const [values, setValues] = useState<Record<string, string> | null>(null);
  const [saved, setSaved] = useState(false);
  const [enqueued, setEnqueued] = useState<number | null>(null);
  const docs = values ?? initial;
  const dirty = DOCS.some(({ key }) => docs[key] !== initial[key]);

  if (isLoading) {
    return (
      <div className="flex justify-center p-10">
        <Spinner />
      </div>
    );
  }
  if (!project) {
    return <div className="p-10 text-center text-gray-500">{t('translationSettings.notFound')}</div>;
  }

  const set = (key: string, value: string) => {
    setSaved(false);
    setValues({ ...docs, [key]: value });
  };

  const handleSave = async () => {
    const changed: ProjectDocs = {};
    DOCS.forEach(({ key }) => {
      if (docs[key] !== initial[key]) changed[key] = docs[key];
    });
    if (Object.keys(changed).length === 0) return;
    await save.mutateAsync(changed);
    setValues(null);
    setSaved(true);
  };

  const handleRegen = async () => {
    setEnqueued(null);
    const count = await regen.mutateAsync();
    setEnqueued(count);
  };

  return (
    <div className="mx-auto max-w-3xl px-4 py-6">
      <nav
        aria-label="Breadcrumb"
        className="mb-4 flex min-w-0 flex-wrap items-center gap-x-1.5 text-sm text-gray-500 dark:text-gray-400"
      >
        <button
          type="button"
          onClick={() => navigate('/books?tab=translations')}
          className="hover:text-gray-700 hover:underline dark:hover:text-gray-200"
        >
          {t('books.tabTranslations')}
        </button>
        <span aria-hidden="true">›</span>
        <button
          type="button"
          onClick={() => navigate(`/translate/${project.id}`)}
          className="min-w-0 truncate hover:text-gray-700 hover:underline dark:hover:text-gray-200"
        >
          {project.title}
        </button>
        <span aria-hidden="true">›</span>
        <span className="text-gray-700 dark:text-gray-300" aria-current="page">
          {t('translationSettings.title')}
        </span>
      </nav>

      <h1 className="mb-1 text-2xl font-semibold text-gray-900 dark:text-gray-100">
        {t('translationSettings.title')}
      </h1>
      <p className="mb-6 text-sm text-gray-500 dark:text-gray-400">
        {t('translationSettings.intro')}
      </p>

      <div className="space-y-6">
        {DOCS.map(({ key, mono, rows }) => (
          <div key={key}>
            <label className="block text-sm font-medium text-gray-800 dark:text-gray-200">
              {t(`translationSettings.docs.${key}.label`)}
            </label>
            <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
              {t(`translationSettings.docs.${key}.help`)}
            </p>
            <textarea
              value={docs[key]}
              rows={rows}
              onChange={(e) => set(key, e.target.value)}
              className={`w-full resize-y rounded-md border border-gray-300 p-3 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 ${
                mono ? 'font-mono' : ''
              }`}
            />
          </div>
        ))}
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-3 border-t border-gray-200 pt-4 dark:border-gray-700">
        <Button onClick={handleSave} disabled={!dirty || save.isPending}>
          {save.isPending ? <Spinner /> : t('translationSettings.save')}
        </Button>
        {saved && !dirty && (
          <span className="text-sm text-green-600 dark:text-green-400">
            {t('translationSettings.saved')}
          </span>
        )}
      </div>

      <div className="mt-8 rounded-md border border-gray-200 p-4 dark:border-gray-700">
        <h2 className="text-sm font-medium text-gray-800 dark:text-gray-200">
          {t('translationSettings.regenTitle')}
        </h2>
        <p className="mb-3 mt-1 text-xs text-gray-500 dark:text-gray-400">
          {t('translationSettings.regenHelp')}
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <Button variant="secondary" onClick={handleRegen} disabled={regen.isPending}>
            {regen.isPending ? <Spinner /> : t('translationSettings.regenButton')}
          </Button>
          {enqueued != null && (
            <span className="text-sm text-gray-600 dark:text-gray-300">
              {t('translationSettings.regenEnqueued', { count: enqueued })}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
